import { Button } from '@rudi/ui/button';
import type { ChatModelOption } from '../api/types';

/**
 * The options whose name or id contains the query, with the selected model kept
 * so its reasoning levels stay available.
 */
export function searchModelOptions(
  options: ChatModelOption[],
  query: string,
  selected: string
): ChatModelOption[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return options;
  return options.filter(
    option =>
      option.model === selected ||
      option.label.toLowerCase().includes(needle) ||
      option.model.toLowerCase().includes(needle)
  );
}

function fetchedLabel(fetchedAt: string | null | undefined): string {
  if (!fetchedAt) return 'Rudi has not loaded OpenRouter’s list yet.';
  const time = new Date(fetchedAt);
  if (Number.isNaN(time.getTime())) return 'Rudi has loaded OpenRouter’s list.';
  return `Listed as OpenRouter offered them at ${time.toLocaleString()}.`;
}

/** Search OpenRouter's models and load its current list from Rudi. */
export function OpenRouterModelSearch({
  label,
  query,
  onQuery,
  count,
  fetchedAt,
  onRefresh,
  refreshing,
  error,
  disabled,
}: {
  label: string;
  query: string;
  onQuery: (query: string) => void;
  count: number;
  fetchedAt: string | null | undefined;
  onRefresh: () => void;
  refreshing: boolean;
  error: string | null;
  disabled: boolean;
}) {
  return (
    <div className="space-y-2">
      <label className="block space-y-1 text-sm">
        <span>Search OpenRouter models</span>
        <input
          type="search"
          aria-label={`${label} OpenRouter model search`}
          value={query}
          disabled={disabled}
          placeholder="Name or id, such as gemma or :free"
          className="h-9 w-full rounded-3xl border border-input bg-background px-3"
          onChange={event => onQuery(event.target.value)}
        />
      </label>
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span>
          {count} {count === 1 ? 'model' : 'models'} with tool calls. {fetchedLabel(fetchedAt)}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || refreshing}
          onClick={onRefresh}
        >
          {refreshing ? 'Loading…' : 'Load current list'}
        </Button>
      </div>
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
